import { Building2, Loader2, Store } from "lucide-react";

import { useShopifyRecipeForm } from "@design-lab/features/shopify-recipe/model/useShopifyRecipeForm";
import { Button } from "@design-lab/shared/ui";
import { Select, SelectItem } from "@design-lab/shared/ui/design-system/Select";

type ShopifyRecipeButtonProps = {
  isAnyLoading: boolean;
  isDesktop: boolean;
};

export function ShopifyRecipeButton({ isAnyLoading, isDesktop }: ShopifyRecipeButtonProps) {
  const {
    stores,
    selectedStoreId,
    setSelectedStoreId,
    isCreatingShopifyRecipe,
    isEditing,
    handleSubmit,
    hasNoStores,
    isDisabled,
    isSelectDisabled,
    tooltipTitle
  } = useShopifyRecipeForm({ isAnyLoading });

  const buttonText = isEditing ? "Update Shopify Recipe" : "Create Shopify Recipe";
  const loadingText = isEditing ? "Updating..." : "Creating...";

  return (
    <>
      <div
        className={
          hasNoStores ? "order-3 cursor-not-allowed lg:order-none" : "order-3 lg:order-none"
        }
        title={tooltipTitle}
      >
        <Select
          data-testid="shopify-store-select"
          aria-label="Select store"
          placeholder="Select Store"
          selectedKey={selectedStoreId}
          onSelectionChange={key => setSelectedStoreId(key == null ? "" : String(key))}
          isDisabled={isSelectDisabled}
          className="h-12 w-40 lg:h-12 xl:h-16 [&_button]:h-full [&_button]:border-green-600"
          items={stores}
          prefix={<Building2 className="size-5" />}
        >
          {store => (
            <SelectItem id={store.id} data-testid="shopify-store-option">
              {store.name}
            </SelectItem>
          )}
        </Select>
      </div>
      <div
        className={
          hasNoStores ? "order-4 cursor-not-allowed lg:order-none" : "order-4 lg:order-none"
        }
        title={tooltipTitle}
      >
        <Button
          data-testid="create-shopify-recipe-button"
          onPress={handleSubmit}
          variant="outline"
          size={isDesktop ? "3xl" : "xl"}
          className="w-full border border-green-600 text-green-600 hover:bg-green-50 lg:h-12 lg:w-auto lg:min-w-48 xl:h-16"
          isDisabled={isDisabled}
        >
          {isCreatingShopifyRecipe ? (
            <Loader2 className="size-5 animate-spin" />
          ) : (
            <Store className="size-5" />
          )}
          {isCreatingShopifyRecipe ? loadingText : buttonText}
        </Button>
      </div>
    </>
  );
}
